/* Product buy row (snippets/theme-qty-select.liquid): select -> stock quantity input, live
   labels ("Add 2 to cart, $128.00"), variant-change refresh of [data-mv-sync], "Added" line,
   companion add. DOM is queried fresh on every event (morphs replace nodes). */
import { formatMoney } from '@theme/money-formatting';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const wrapEl = () => document.querySelector('[data-mv-qty-wrap]');
const sectionOf = (el) => el?.closest('.shopify-section') || document;

function cfg() {
  const w = wrapEl();
  if (!w) return null;
  const s = sectionOf(w);
  return {
    w,
    s,
    select: w.querySelector('[data-mv-qty]'),
    form: s.querySelector('product-form-component'),
    format: w.dataset.mvMoneyFormat || '${{amount}}',
    currency: w.dataset.mvCurrency || 'USD',
    one: w.dataset.mvOne || '',
    many: w.dataset.mvMany || '',
    breaks: (w.dataset.mvBreaks || '')
      .split(',')
      .map((p) => p.split(':').map((n) => parseInt(n, 10)))
      .filter(([m, p]) => m > 1 && p > 0 && p < 100)
      .sort((a, b) => a[0] - b[0]),
  };
}

const money = (c, cents) => formatMoney(cents, c.format, c.currency);

function pctFor(c, qty) {
  let pct = 0;
  for (const [min, p] of c.breaks) if (qty >= min) pct = p;
  return pct;
}

function companion(c) {
  const box = c.s.querySelector('[data-mv-companion]');
  return box && box.checked ? box : null;
}

function totals(c) {
  const qty = parseInt(c.select?.value || '1', 10) || 1;
  const price = parseInt(c.s.querySelector('[data-mv-price]')?.dataset.mvPrice || '0', 10);
  const sub = price * qty;
  let total = sub - Math.round((sub * pctFor(c, qty)) / 100);
  const comp = companion(c);
  if (comp) total += parseInt(comp.dataset.mvCompanionPrice || '0', 10);
  return { qty, price, total, comp };
}

function labelFor(c, t) {
  const amount = money(c, t.total);
  if (t.comp) return t.qty === 1 ? `Add both to cart, ${amount}` : `Add ${t.qty + 1} items to cart, ${amount}`;
  return t.qty === 1 ? `Add to cart, ${amount}` : `Add ${t.qty} to cart, ${amount}`;
}

function setText(button, text) {
  if (!button || button.disabled) return;
  const node = [...button.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
  if (node) {
    if (node.textContent.trim() !== text) node.textContent = ` ${text} `;
  } else {
    button.insertBefore(document.createTextNode(` ${text} `), button.querySelector('.add-to-cart__added'));
  }
}

function stockInput(c) {
  return c.form?.querySelector('quantity-selector-component input[name="quantity"]');
}

function apply(dispatch = false) {
  const c = cfg();
  if (!c || !c.select) return;
  const t = totals(c);
  if (!t.price) return;

  const stockWrap = c.form?.querySelector('.quantity-selector-wrapper');
  if (stockWrap && !stockWrap.inert) stockWrap.inert = true;

  // Clamp to the stock input's max (quantity rules, stock).
  const input = stockInput(c);
  const max = parseInt(input?.max || '0', 10);
  for (const o of c.select.options) o.disabled = max > 0 && parseInt(o.value, 10) > max;
  if (max > 0 && t.qty > max) {
    c.select.value = String(max);
    return apply(true);
  }

  if (input && input.value !== String(t.qty)) {
    input.value = String(t.qty);
    dispatch = true;
  }
  if (dispatch && input) {
    input.closest('quantity-selector-component')?.dispatchEvent(new QuantitySelectorUpdateEvent(t.qty));
  }

  const label = labelFor(c, t);
  setText(c.form?.querySelector('[ref="addToCartButton"]'), label);
  const tab = document.querySelector('[data-mv-tab-add]');
  if (tab && !c.form?.querySelector('[ref="addToCartButton"]')?.disabled) tab.textContent = label;
  const tabPrice = document.querySelector('[data-mv-tab-price]');
  if (tabPrice) tabPrice.textContent = money(c, t.price);

  const live = c.s.querySelector('[data-mv-live-total]');
  const word = t.qty === 1 ? c.one : c.many;
  const liveText = `${word ? `${t.qty} ${word}` : `Quantity ${t.qty}`}, ${money(c, t.total)}`;
  if (live && live.textContent !== liveText) live.textContent = liveText;

  // Price table: the row that applies to the chosen quantity.
  const rows = [...c.s.querySelectorAll('[data-mv-break-min]')];
  let current = null;
  for (const r of rows) if (t.qty >= parseInt(r.dataset.mvBreakMin, 10)) current = r;
  for (const r of rows) r.classList.toggle('is-current', r === current);

  // Companion: express checkout buys one item.
  const express = c.form?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
  const note = c.s.querySelector('[data-mv-companion-note]');
  if (note) note.hidden = !t.comp;
  if (express) {
    if (t.comp) {
      express.dataset.mvHiddenByCompanion = 'true';
      express.hidden = true;
    } else if (express.dataset.mvHiddenByCompanion) {
      delete express.dataset.mvHiddenByCompanion;
      express.hidden = !!c.form?.querySelector('[ref="addToCartButton"]')?.disabled;
    }
  }
}

document.addEventListener('change', (e) => {
  const el = e.target;
  if (!(el instanceof Element)) return;
  if (el.matches('[data-mv-qty]')) apply(true);
  else if (el.matches('[data-mv-companion]')) apply();
});

document.addEventListener('click', (e) => {
  const el = e.target instanceof Element ? e.target.closest('[data-mv-break-set]') : null;
  if (el) {
    const c = cfg();
    if (!c?.select) return;
    const v = el.getAttribute('data-mv-break-set');
    if ([...c.select.options].some((o) => o.value === v && !o.disabled)) {
      c.select.value = v;
      apply(true);
    }
    return;
  }
  const tab = e.target instanceof Element ? e.target.closest('[data-mv-tab-add]') : null;
  if (tab) cfg()?.form?.querySelector('[ref="addToCartButton"]')?.click();
});

document.addEventListener(
  StandardEvents.productSelect,
  (event) => {
    event.promise
      ?.then(({ detail }) => {
        setTimeout(() => {
          const html = detail?.html;
          if (html) {
            for (const el of document.querySelectorAll('[data-mv-sync][id]')) {
              const next = html.getElementById?.(el.id) || html.querySelector?.(`#${CSS.escape(el.id)}`);
              if (next) el.replaceWith(next.cloneNode(true));
            }
          }
          apply(true);
        }, 0);
      })
      .catch(() => {});
  },
  true
);

// "Added" line after a successful add from this page.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action && event.action !== 'add') return;
  event.promise
    ?.then(({ cart, detail }) => {
      const c = cfg();
      const line = c?.s.querySelector('[data-mv-added]');
      if (!line || detail?.didError) return;
      const n = (event.lines || []).reduce((sum, l) => sum + (Number(l.quantity) || 0), 0) || detail?.itemCount || 1;
      line.classList.remove('is-error');
      line.querySelector('[data-mv-added-text]').textContent = `Added ${n} to your cart.`;
      const count = cart?.totalQuantity;
      line.querySelector('[data-mv-added-count]').textContent = count ? ` (${count})` : '';
      line.querySelector('[data-mv-added-link]').hidden = false;
      line.hidden = false;
    })
    .catch(() => {});
});

function showError(c, message) {
  const line = c.s.querySelector('[data-mv-added]');
  if (!line) return;
  line.classList.add('is-error');
  line.querySelector('[data-mv-added-text]').textContent = `Error: ${message}`;
  line.querySelector('[data-mv-added-link]').hidden = true;
  line.hidden = false;
}

/* Cart drawer: ask cart/add.js for the drawer section (<cart-items-component
   data-section-id>) so it re-renders from the same response; the drawer opens itself
   on the 'add' event (<cart-drawer-component auto-open>). */
function withDrawerSections(body) {
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.getAttribute('data-section-id'))
    .filter(Boolean);
  if (!ids.length) return body;
  return { ...body, sections: [...new Set(ids)].join(','), sections_url: window.location.pathname };
}

function closeDrawer() {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (drawer?.isOpen) drawer.close();
}

// Companion ticked: add both in one request (capture phase, ahead of the stock handler).
window.addEventListener(
  'submit',
  async (e) => {
    const c = cfg();
    const form = e.target;
    if (!c || !(form instanceof HTMLFormElement) || !c.form?.contains(form)) return;
    const t = totals(c);
    if (!t.comp) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const id = form.querySelector('input[name="id"]')?.value;
    if (!id) return;
    const lines = [
      { merchandiseId: id, quantity: t.qty },
      { merchandiseId: t.comp.dataset.mvCompanionVariant, quantity: 1 },
    ];
    const deferred = CartLinesUpdateEvent.createPromise();
    c.form.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise }));
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(
          withDrawerSections({ items: lines.map((l) => ({ id: Number(l.merchandiseId), quantity: l.quantity })) })
        ),
      });
      const body = await res.json();
      const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
      const failed = !res.ok || body.status;
      if (failed) {
        c.form.dispatchEvent(new CartErrorEvent({ error: body.message || 'Add to cart failed', code: 'INVALID' }));
        closeDrawer();
        showError(c, body.description || body.message || 'The items could not be added.');
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: failed,
          itemCount: t.qty + 1,
          items: cart.items,
          sections: failed ? undefined : body.sections || undefined,
          source: 'theme-buy',
        },
      });
    } catch (err) {
      deferred.reject(err);
      closeDrawer();
      showError(c, 'The items could not be added. Please try again.');
    }
  },
  true
);

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => apply(), { once: true });
else apply();
